// components/NotificationBell.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import api from '../api/axios';

interface NotificationItem {
  id: number;
  type: string;
  title_ja: string | null;
  title_zh: string | null;
  body_ja: string | null;
  body_zh: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

interface NotificationListOut {
  items: NotificationItem[];
  unread_count: number;
}

type TabKey = 'all' | 'system' | 'doctor' | 'admin';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function timeAgo(createdAt: string, t: (key: string, opts?: any) => string): string {
  const utcStr = createdAt.endsWith('Z') || createdAt.includes('+') ? createdAt : createdAt + 'Z';
  const diff = Math.floor((Date.now() - new Date(utcStr).getTime()) / 1000);
  if (diff < 60) return t('notification.just_now');
  if (diff < 3600) return t('notification.minutes_ago', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('notification.hours_ago', { count: Math.floor(diff / 3600) });
  return t('notification.days_ago', { count: Math.floor(diff / 86400) });
}

function tabFilter(item: NotificationItem, tab: TabKey): boolean {
  if (tab === 'all') return true;
  if (tab === 'doctor') return item.type === 'message';
  if (tab === 'admin') return item.type === 'admin_message';
  return item.type !== 'message' && item.type !== 'admin_message';
}

const NotificationBell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationListOut>({ items: [], unread_count: 0 });
  const [tab, setTab] = useState<TabKey>('all');
  const [modalItem, setModalItem] = useState<NotificationItem | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  // ドロップダウンの実座標（fixed 配置）。ナビバーは sticky ではないので
  // ベルの実位置を毎回計測する。スマホ/WeChat 内ブラウザでの画面外はみ出し対策。
  const [pos, setPos] = useState<{ top: number; right: number; maxWidth: number; maxHeight: number } | null>(null);
  const isJa = i18n.language === 'ja';

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get<NotificationListOut>('/notifications?limit=50');
      setData(res.data);
    } catch {
      // 未ログイン時などは無視
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') fetchNotifications();
    }, 60000);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ベルの実位置からドロップダウン座標を算出（余白 12px を常に確保）
  const GUTTER = 12;
  const PANEL_MAX_H = 420;  // PC での見え方を従来（リスト max-h-80）と揃える上限
  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // スマホは画面幅いっぱいのシート風、PC は従来どおりベル右端に揃える
    const right = vw < 640 ? GUTTER : Math.max(GUTTER, vw - r.right);
    const top = r.bottom + 4;
    const next = {
      top,
      right,
      maxWidth: Math.max(240, vw - right - GUTTER),
      maxHeight: Math.max(200, Math.min(PANEL_MAX_H, vh - top - GUTTER)),
    };
    // scroll は capture=true でリスト自身のスクロールも拾うため、
    // 値が変わらないときは再レンダーさせない（スクロール時のカクつき防止）
    setPos(prev =>
      prev &&
      prev.top === next.top &&
      prev.right === next.right &&
      prev.maxWidth === next.maxWidth &&
      prev.maxHeight === next.maxHeight
        ? prev
        : next
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    // スクロール（capture=true で祖先のスクロールも拾う）／回転・リサイズに追従
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, updatePos]);

  const markRead = async (item: NotificationItem) => {
    if (!item.read_at) {
      try {
        await api.patch(`/notifications/${item.id}/read`);
        setData(prev => ({
          items: prev.items.map(n => n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n),
          unread_count: Math.max(0, prev.unread_count - 1),
        }));
      } catch { /* 失敗しても続ける */ }
    }
  };

  const handleClick = async (item: NotificationItem) => {
    await markRead(item);
    setOpen(false);
    if (item.link) {
      navigate(item.link);
    } else {
      setModalItem(item);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setData(prev => ({
        items: prev.items.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })),
        unread_count: 0,
      }));
    } catch { /* 無視 */ }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all',    label: isJa ? 'すべて'  : '全部' },
    { key: 'system', label: isJa ? 'システム' : '系统' },
    { key: 'doctor', label: isJa ? '医師'    : '医生' },
    { key: 'admin',  label: isJa ? '管理員'  : '管理员' },
  ];

  const filtered = data.items.filter(item => tabFilter(item, tab));
  const tabUnread = (k: TabKey) => data.items.filter(i => !i.read_at && tabFilter(i, k)).length;

  return (
    <>
      {/* 全文表示モーダル */}
      {modalItem && (() => {
        const title = (isJa ? modalItem.title_ja : modalItem.title_zh) ?? modalItem.title_ja ?? '';
        const body  = (isJa ? modalItem.body_ja  : modalItem.body_zh)  ?? modalItem.body_ja  ?? '';
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[80vh]">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-bold text-gray-800">{title}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(modalItem.created_at, t)}</p>
              </div>
              <div className="px-5 py-4 overflow-y-auto flex-1">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{body}</p>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setModalItem(null)}
                  className="px-5 py-2 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral/90 transition"
                >
                  {t('notification.close')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="relative" ref={dropdownRef}>
        {/* 鈴アイコン */}
        <button
          ref={btnRef}
          onClick={() => {
            const next = !open;
            if (next) { updatePos(); fetchNotifications(); }  // 開く前に座標確定（チラつき防止）
            setOpen(next);
          }}
          className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label={t('notification.bell_title')}
        >
          <Bell size={20} className="text-gray-600" />
          {data.unread_count > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
              {data.unread_count > 99 ? '99+' : data.unread_count}
            </span>
          )}
        </button>

        {/* ドロップダウン */}
        {open && (
          <div
            className="fixed w-[420px] flex flex-col bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
            style={pos ? { top: pos.top, right: pos.right, maxWidth: pos.maxWidth, maxHeight: pos.maxHeight } : undefined}
          >
            {/* ヘッダー */}
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-bold text-gray-800">{t('notification.bell_title')}</span>
              {data.unread_count > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-blue-500 hover:underline">
                  {t('notification.mark_all_read')}
                </button>
              )}
            </div>

            {/* タブバー（狭い画面でも横スクロールで全タブに到達できるようにする） */}
            <div className="shrink-0 flex border-b border-gray-100 px-2 pt-1 overflow-x-auto scrollbar-hide">
              {tabs.map(({ key, label }) => {
                const cnt = tabUnread(key);
                return (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`relative flex shrink-0 items-center gap-1 px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      tab === key
                        ? 'text-coral border-coral'
                        : 'text-gray-400 border-transparent hover:text-gray-600'
                    }`}
                  >
                    {label}
                    {cnt > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full">
                        {cnt}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 通知リスト */}
            <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <li className="py-10 text-center text-sm text-gray-400">
                  {isJa ? 'お知らせはありません' : '暂无通知'}
                </li>
              ) : (
                filtered.map(item => {
                  const title = (isJa ? item.title_ja : item.title_zh) ?? item.title_ja ?? '';
                  const body  = (isJa ? item.body_ja  : item.body_zh)  ?? item.body_ja  ?? '';
                  const isUnread = !item.read_at;
                  return (
                    <li
                      key={item.id}
                      onClick={() => handleClick(item)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${isUnread ? 'bg-blue-50/40' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {isUnread && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                        )}
                        <div className={`min-w-0 break-words ${isUnread ? '' : 'ml-4'}`}>
                          <p className="text-xs font-semibold text-gray-800">{title}</p>
                          {body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{body}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">{timeAgo(item.created_at, t)}</p>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationBell;
